import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { Sizes } from 'components/domain/ProfilingForm/CheckboxButtonChoice';
import { NsKey } from 'enums/NsKey';
import { useDialog } from 'hooks/useDialog';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import SelectBrokerBranches from 'features/dashboard/dashboard-menu/SelectBrokerBranches';

interface RefusedQuoteBannerProps {
  /** Message the validator left with the refusal, if any. */
  validatorMessage?: string | null;
}

/**
 * Banner shown above the contractualization when the validator refused the quote. Like the
 * cancelled quote banner it is persistent: the quote is blocked for good. The broker can
 * start over with a new simulation, opened the same way as from the "Créer" menu.
 */
export const RefusedQuoteBanner = ({ validatorMessage }: RefusedQuoteBannerProps) => {
  const { t } = useTranslation(NsKey.Contractualization);
  const { openBlankDialog } = useDialog();
  // Same right as the "Créer" menu that offers the simulation creation.
  const canCreate = useRoleCan(Capability.CreateMenuAccess);

  const onNewSimulation = useCallback(() => {
    openBlankDialog({ size: Sizes['5xl'] }, <SelectBrokerBranches />);
  }, [openBlankDialog]);

  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4"
    >
      <Ban aria-hidden="true" className="w-5 h-5 mt-0.5 text-red-600 flex-shrink-0" />
      <div className="flex flex-1 flex-col gap-1">
        <p className="font-semibold text-red-800">
          {t('CONTRACTUALIZATION.CARDS.VALIDATION_REFUSED_BANNER.TITLE')}
        </p>
        <p className="text-sm text-red-700">
          {t('CONTRACTUALIZATION.CARDS.VALIDATION_REFUSED_BANNER.DESCRIPTION')}
        </p>
        {validatorMessage && (
          <p className="whitespace-pre-line text-sm text-red-700">
            {t('CONTRACTUALIZATION.CARDS.VALIDATION_REFUSED_BANNER.VALIDATOR_MESSAGE', {
              message: validatorMessage
            })}
          </p>
        )}
      </div>
      {canCreate && (
        <Button color="secondary" round onClick={onNewSimulation} className="flex-shrink-0">
          {t('CONTRACTUALIZATION.CARDS.VALIDATION_REFUSED_BANNER.NEW_SIMULATION')}
        </Button>
      )}
    </div>
  );
};
